import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import { programStatusChoices, relationshipChoices } from '@/const/fellowChoices'
import type { ToastServiceMethods } from 'primevue/toastservice'

export interface FellowDataUpdateCallbacks {
  onSuccess?: () => void
  onError?: () => void
  onFinally?: () => void
  toast?: ToastServiceMethods
}

interface Adventurer {
  outtalent_program_status: string
  outtalent_relationship: string
  profile_details?: {
    user_details?: {
      first_name?: string
    }
  }
  [key: string]: any
}

export const handleProgramStatusChange = async (
  username: string,
  value: unknown,
  adventurer: Adventurer,
  callbacks?: FellowDataUpdateCallbacks
) => {
  const key = value as keyof typeof programStatusChoices | null
  if (!adventurer || !key) return

  const firstName = adventurer.profile_details?.user_details?.first_name || 'Adventurer'

  try {
    await api.patch(API_ROUTES.EDIT_FELLOW_DATA(username), {
      outtalent_program_status: key
    })
    adventurer.outtalent_program_status = programStatusChoices[key]
    
    callbacks?.toast?.add({
      severity: 'success',
      summary: 'Program Status Updated',
      detail: `${firstName}'s program status has been updated to ${programStatusChoices[key]}`,
      life: 3000
    })
    
    callbacks?.onSuccess?.()
  } catch (err) {
    console.error('Failed to update program status:', err)
    
    callbacks?.toast?.add({
      severity: 'error',
      summary: 'Update Failed',
      detail: 'Failed to update program status. Please try again.',
      life: 3000
    })
    
    callbacks?.onError?.()
  } finally {
    callbacks?.onFinally?.()
  }
}

export const handleRelationshipChange = async (
  username: string,
  value: unknown,
  adventurer: Adventurer,
  callbacks?: FellowDataUpdateCallbacks
) => {
  const key = value as keyof typeof relationshipChoices | null
  if (!adventurer || !key) return

  const firstName = adventurer.profile_details?.user_details?.first_name || 'Adventurer'

  try {
    await api.patch(API_ROUTES.EDIT_FELLOW_DATA(username), {
      outtalent_relationship: key
    })
    adventurer.outtalent_relationship = relationshipChoices[key]
    
    callbacks?.toast?.add({
      severity: 'success',
      summary: 'Relationship Status Updated',
      detail: `${firstName}'s relationship status has been updated to ${relationshipChoices[key]}`,
      life: 3000
    })
    
    callbacks?.onSuccess?.()
  } catch (err) {
    console.error('Failed to update relationship status:', err)
    
    callbacks?.toast?.add({
      severity: 'error',
      summary: 'Update Failed',
      detail: 'Failed to update relationship status. Please try again.',
      life: 3000
    })
    
    callbacks?.onError?.()
  } finally {
    callbacks?.onFinally?.()
  }
}

export const getUserStatus = (user: any) => {
  if (user.left_tasks_count > 0) {
    if (percentageDone(user) < 50) {
      return { text: 'Falling behind', cssClass: 'bg-red-100 text-red-800' }
    } else {
      return { text: 'Catching up', cssClass: 'bg-yellow-100 text-yellow-800' }
    }
  } else if (user.ahead_tasks_done_count > 0) {
    return { text: 'Ahead', cssClass: 'bg-lime-200 text-lime-800' }
  } else {
    return { text: 'On Track', cssClass: 'bg-cosmicPurple/10 text-cosmicPurple' }
  }
}

export function percentageDone(user: any) {
  const totalTasks = user.current_week_done_tasks_count + user.left_tasks_count
  if (totalTasks === 0) {
    return 0
  }

  const percentage = (user.current_week_done_tasks_count * 100) / totalTasks
  return typeof percentage === 'number' ? percentage : 0
} 